import { useEffect, useRef, useState, useCallback } from 'react'
import { useLocation } from 'react-router'
import { Send, X } from 'lucide-react'
import { useLanguage } from '../context/LanguageContext'

const STORAGE_KEY = 'ng-newsletter-popup-seen'
const REQUEST_TIMEOUT_MS = 12000
const DEFAULT_SHOW_DELAY_MS = 60000
const rawDelay = Number(import.meta.env.VITE_NEWSLETTER_POPUP_DELAY_MS)
const SHOW_DELAY_MS = Number.isFinite(rawDelay) && rawDelay >= 0 ? rawDelay : DEFAULT_SHOW_DELAY_MS

const normalizeText = (value) => String(value || '').replace(/\s+/g, ' ').trim()

export default function DelayedNewsletterPopup() {
  const { t } = useLanguage()
  const location = useLocation()
  const [visible, setVisible] = useState(false)
  const [entered, setEntered] = useState(false)
  const [email, setEmail] = useState('')
  const [consent, setConsent] = useState(false)
  const [sending, setSending] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')
  const timerRef = useRef(null)
  const modalRef = useRef(null)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (location.pathname.startsWith('/newsletter')) return
    const seen = window.localStorage.getItem(STORAGE_KEY)

    if (seen) return

    // if URL contains ?popup=1 force immediate show (helpful for testing)
    try {
      const url = new URL(window.location.href)
      if (url.searchParams.get('popup') === '1') {
        const frame = window.requestAnimationFrame(() => {
          setVisible(true)
        })
        return () => window.cancelAnimationFrame(frame)
      }
    } catch {
      // ignore
    }

    timerRef.current = window.setTimeout(() => {
      setVisible(true)
    }, SHOW_DELAY_MS)

    return () => clearTimeout(timerRef.current)
  }, [location.pathname, location.search])

  const handleClose = useCallback((persist = false) => {
    setVisible(false)
    if (persist && typeof window !== 'undefined') {
      // persist explicit dismissal permanently in localStorage
      window.localStorage.setItem(STORAGE_KEY, '1')
    }
  }, [])

  const handleTemporaryClose = useCallback(() => {
    // close without persisting; will reappear after reload
    handleClose(false)
  }, [handleClose])

  const handlePermanentDismiss = useCallback(() => {
    // user explicitly opted out
    handleClose(true)
  }, [handleClose])

  useEffect(() => {
    if (!visible) return
    const frame = window.requestAnimationFrame(() => setEntered(true))
    const previousActive = document.activeElement
    const input = modalRef.current?.querySelector('input[type="email"]')
    input?.focus()

    const onKey = (e) => {
      if (e.key === 'Escape') handleClose(false)
    }
    document.addEventListener('keydown', onKey)
    const onShowEvent = () => setVisible(true)
    window.addEventListener('ng-show-newsletter', onShowEvent)
    // expose a developer helper to force the popup (only in dev)
    if (import.meta.env.DEV) {
      // attach a safe dispatcher
      window.__ng_showNewsletterPopup = () => window.dispatchEvent(new Event('ng-show-newsletter'))
    }
    return () => {
      document.removeEventListener('keydown', onKey)
      window.cancelAnimationFrame(frame)
      setEntered(false)
      window.removeEventListener('ng-show-newsletter', onShowEvent)
      if (import.meta.env.DEV) {
        try { delete window.__ng_showNewsletterPopup } catch { /* ignore */ }
      }
      previousActive?.focus()
    }
  }, [visible, handleClose])


  const newsletterEndpoint = import.meta.env.VITE_NEWSLETTER_ENDPOINT || '/api/newsletter'

  const handleSubmit = async (e) => {
    e.preventDefault()
    const cleanEmail = normalizeText(email).toLowerCase().slice(0, 254)

    if (!consent) { setError(t('newsletter.errorConsent') || 'Veuillez accepter le RGPD'); return }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) { setError('Adresse email invalide.'); return }

    setSending(true)
    setError('')

    try {
      const payload = {
        email: cleanEmail,
        rgpdAccepted: true,
        source: 'delayed-popup',
        submittedAt: new Date().toISOString(),
      }

      const abortController = new AbortController()
      const timeout = setTimeout(() => abortController.abort(), REQUEST_TIMEOUT_MS)

      const response = await fetch(newsletterEndpoint, {
        method: 'POST',
        mode: 'cors',
        cache: 'no-store',
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
        signal: abortController.signal,
      })

      clearTimeout(timeout)

      if (!response.ok) {
        let msg = `Échec de l'inscription à la newsletter. (${response.status})`
        try {
          const contentType = response.headers.get('content-type') || ''
          if (contentType.includes('application/json')) {
            const data = await response.json()
            if (data && data.error) msg += ` ${data.error}`
            else if (data && data.message) msg += ` ${data.message}`
          } else {
            const text = await response.text()
            if (text) msg += ` ${text}`
          }
        } catch {
          // ignore parsing errors
        }
        throw new Error(msg)
      }

      setSubmitted(true)
      setEmail('')
      setConsent(false)
      if (typeof window !== 'undefined') window.localStorage.setItem(STORAGE_KEY, '1')
    } catch (submitError) {
      if (submitError.name === 'AbortError') {
        setError("Le serveur met trop de temps à répondre. Merci de réessayer.")
      } else {
        setError(submitError.message || "Une erreur est survenue. Merci de réessayer.")
      }
    } finally {
      setSending(false)
    }
  }

  if (location.pathname.startsWith('/newsletter') || !visible) return null

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/45 backdrop-blur-[2px]" onClick={() => handleTemporaryClose()} aria-hidden="true" />

      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('newsletter.title') || 'Newsletter'}
        aria-describedby="newsletter-popup-description"
        onClick={(e) => e.stopPropagation()}
        className={`relative z-10 max-w-lg w-full bg-white rounded-2xl p-6 shadow-2xl ring-1 ring-ng-dark/10 pointer-events-auto transform transition-all duration-300 ease-out ${entered ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-95'}`}
      >
        <button
          type="button"
          aria-label="Fermer"
          onClick={() => handleTemporaryClose()}
          className="absolute top-4 right-4 p-1 rounded-md text-ng-dark hover:bg-ng-dark/5"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-4 mb-3">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-ng-dark/10 flex-shrink-0">
            <svg className="w-5 h-5 text-ng-dark" viewBox="0 0 24 24" fill="currentColor"><path d="M2 4.5A2.5 2.5 0 0 1 4.5 2h15A2.5 2.5 0 0 1 22 4.5v15a2.5 2.5 0 0 1-2.5 2.5h-15A2.5 2.5 0 0 1 2 19.5v-15zM5 7v10h14V7H5zm2 2h10v2H7V9z"/></svg>
          </div>
          <div>
            <h3 className="font-display text-lg font-semibold text-ng-dark">{t('newsletter.title')}</h3>
            <p id="newsletter-popup-description" className="text-sm text-ng-dark/80 leading-relaxed">{t('newsletter.subtitle')}</p>
          </div>
        </div>

        {submitted ? (
          <div className="p-4 bg-ng-mid/10 border border-ng-mid/30 rounded-lg text-ng-mid font-medium">
            {t('newsletter.success')}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-3 items-center">
              <input
                type="email"
                placeholder={t('newsletter.placeholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 px-4 py-3 rounded-xl border border-ng-dark/20 outline-none focus:border-ng-dark focus:ring-2 focus:ring-ng-dark/20 transition-all"
              />
              <button
                type="submit"
                disabled={sending}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-ng-dark text-white rounded-xl hover:bg-ng-mid transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
                {sending ? 'Envoi...' : t('newsletter.submit')}
              </button>
            </div>

            <label className="flex items-start gap-3 text-left cursor-pointer">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 w-4 h-4 rounded accent-ng-dark cursor-pointer"
              />
              <span className="text-xs text-ng-dark/80 leading-relaxed">
                {t('newsletter.consent')}{' '}
                <a href="/politique-de-confidentialite" className="underline hover:text-ng-dark">{t('common.privacy')}</a>
              </span>
            </label>

            {error && <p className="text-red-500 text-sm">{error}</p>}

            <div className="mt-3 flex">
              <div className="flex-1" />
              <button
                type="button"
                onClick={() => handlePermanentDismiss()}
                className="text-sm text-ng-dark/70 hover:text-ng-dark px-2 py-1"
              >
                {t('newsletter.noThanks') || 'Non merci'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
